import { useEffect, useState, useCallback } from "react";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";

import { PageContainer } from "components/PageContainer";
import { DataTable, ColumnProps } from "components/Table";
import { Toolbar } from "components/Toolbar";
import CustomCheckBox from "components/checkbox";
import { FormTableLine } from "./FormTable";
import { ConfirmExportPopup, ExportDestination, IConfirmExportPopup } from "./ConfirmExportPopup";
import RefreshIcon from "@mui/icons-material/Refresh";

import { MainContainer } from "./styles";
import { routeService } from "services/routeService";
import { abacateService } from "services/abacateApi";
import { chunkArray } from "utils/chunkArray";
import { verifyDate } from "utils/verifyDate";

import { ExportRoutesResponse, IOrderRoute } from "interfaces/IWaybill";
import { IAgileRoute } from "interfaces/IAgileRouteResponse";

export const ExportRoutes = () => {
  const smallScreen = useMediaQuery({ maxWidth: 820 });
  const [data, setData] = useState<IAgileRoute[]>([]);
  const [filteredData, setFilteredData] = useState<IAgileRoute[]>([]);
  const [dataOrderRoute, setDataOrderRoute] = useState<IOrderRoute[] | null>(null);
  const [maracujaRouteCounts, setMaracujaRouteCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isCheckedAll, setIsCheckedAll] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState<IConfirmExportPopup>({
    visible: false,
    routeCount: 0,
  });
  const [waitingCarambola, setWaitingCarambola] = useState(false);
  const [waitingMaracuja, setWaitingMaracuja] = useState(false);
  const [responsesCarambola, setResponsesCarambola] = useState<ExportRoutesResponse | null>(null);
  const [responsesMaracuja, setResponsesMaracuja] = useState<ExportRoutesResponse | null>(null);

  const [params, setParams] = useState({
    deliveryDate: verifyDate()
  });

  const fetchMaracujaRouteCounts = useCallback(async (routes: IAgileRoute[]) => {
    if (routes.length === 0) return {};

    const initialCounts = Object.fromEntries(routes.map(({ routeId }) => [routeId, 0]));
    const response = await routeService.getRouteTagCounts({
      deliveryDate: params.deliveryDate,
    });

    return routes.reduce<Record<number, number>>((acc, { routeId }) => ({
      ...acc,
      [routeId]: response[routeId] ?? 0,
    }), initialCounts);
  }, [params.deliveryDate]);

  const refreshCounters = useCallback(async () => {
    setLoadingCounters(true);
    try {
      const [orderRoute, routeTagCounts] = await Promise.all([
        abacateService.getOrderRoute({ scheduled_date: params.deliveryDate }),
        fetchMaracujaRouteCounts(data),
      ]);
      setDataOrderRoute(orderRoute);
      setMaracujaRouteCounts(routeTagCounts);
    } catch {
      toast.error("Erro ao atualizar contadores.");
    } finally {
      setLoadingCounters(false);
    }
  }, [data, fetchMaracujaRouteCounts, params.deliveryDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [routes, orderRoute] = await Promise.all([
        routeService.getAgileRoutes(params.deliveryDate),
        abacateService.getOrderRoute({ scheduled_date: params.deliveryDate }),
      ]);

      if (routes.length === 0) {
        toast.warning("Não foram encontradas rotas para este dia.");
        setData([]);
        setFilteredData([]);
        return;
      }

      const filteredRoutes = routes.filter(({ depot }: IAgileRoute) =>
        depot?.depotName !== "CD Compras" && depot?.depotName !== "CD Plantei"
      );

      setData(filteredRoutes);
      setFilteredData(filteredRoutes);
      setDataOrderRoute(orderRoute);
      setMaracujaRouteCounts(await fetchMaracujaRouteCounts(filteredRoutes));
    } catch {
      toast.error("Erro ao buscar dados. Por favor, tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    const lowerCaseQuery = query.toLowerCase();
    const filtered = data.filter(({ vehicle, routeId }: IAgileRoute) =>
      vehicle?.driver?.name?.toLowerCase().includes(lowerCaseQuery) ||
      routeId.toString().includes(lowerCaseQuery) ||
      vehicle?.name?.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredData(filtered);
  };

  const handleToggleSelected = (routeId: number) => {
    setSelectedRows(prev =>
      prev.includes(routeId) ? prev.filter(id => id !== routeId) : [...prev, routeId]
    );
  };

  const handleToggleAllSelected = () => {
    const newState = !isCheckedAll;
    setIsCheckedAll(newState);
    setSelectedRows(newState ? filteredData.map(({ routeId }: IAgileRoute) => routeId) : []);
  };

  const isRouteVersionError = (error: any) =>
    error?.error === "route_outdated" || error?.error === "missing_route_version";

  // Trata a rejeição da exportação quando o back-end barra uma rota
  // desatualizada / sem versão. Interrompe o restante do envio (o throw já saiu
  // do loop de lotes), avisa o usuário e recarrega as rotas para substituir a
  // cópia desatualizada mantida em memória. Retorna true se tratou o erro.
  const handleExportVersionError = (error: any): boolean => {
    if (!isRouteVersionError(error)) return false;
    toast.warning(
      error?.message ??
        "Uma das rotas está desatualizada. Recarregue a página e exporte novamente."
    );
    fetchData();
    return true;
  };

  const executeExportCarambola = async () => {
    toast.info("Aguarde, enviando para o Carambola...");
    setWaitingCarambola(true);
    setResponsesCarambola(null);

    try {
      const chunks = chunkArray(selectedRows, 3);
      for (const chunk of chunks) {
        const bodyList = chunk.map(routeId => data.find(route => route.routeId === routeId));
        const response = await routeService.exportRoutes(bodyList);
        setResponsesCarambola(prev => ({ ...prev, ...response.responses }));
      }
      toast.success("Envio para o Carambola finalizado. Verifique o resultado.");
      refreshCounters();
    } catch (error) {
      if (!handleExportVersionError(error)) {
        toast.error("Houve um erro ao exportar para o Carambola. Por favor, tente novamente mais tarde.");
      }
    } finally {
      setWaitingCarambola(false);
      setSelectedRows([]);
      setIsCheckedAll(false);
    }
  };

  const executeExportMaracuja = async () => {
    toast.info("Aguarde, enviando para o Maracujá...");
    setWaitingMaracuja(true);
    setResponsesMaracuja(null);

    try {
      const chunks = chunkArray(selectedRows, 3);
      for (const chunk of chunks) {
        const bodyList = chunk.map(routeId => data.find(route => route.routeId === routeId));
        const response = await routeService.exportToMaracuja(bodyList);
        setResponsesMaracuja(prev => ({ ...prev, ...response.responses }));
      }
      toast.success("Envio para o Maracujá finalizado. Verifique o resultado.");
      refreshCounters();
    } catch (error) {
      if (!handleExportVersionError(error)) {
        toast.error("Houve um erro ao exportar para o Maracujá. Por favor, tente novamente mais tarde.");
      }
    } finally {
      setWaitingMaracuja(false);
      setSelectedRows([]);
      setIsCheckedAll(false);
    }
  };

  const handleExport = () => {
    setConfirmPopup({
      visible: true,
      routeCount: selectedRows.length,
    });
  };

  const handleConfirmExport = (destination: ExportDestination) => {
    if (destination === "Carambola") {
      executeExportCarambola();
      return;
    }

    executeExportMaracuja();
  };

  const handleChangeParams = (newParams: typeof params) => {
    setParams(prev => ({ ...prev, ...newParams }));
    setSelectedRows([]);
    setIsCheckedAll(false);
  };

  const defaultColumns: ColumnProps[] = [
    {
      name: <CustomCheckBox handleToggle={handleToggleAllSelected} align='center' checked={isCheckedAll === true} />,
      align: "center",
      minWidth: "80px"
    },
    {
      name: "Motorista",
      align: "left",
    },
    {
      name: "Rota",
      align: "center",
    },
    {
      name: "Centro de Distribuição",
      align: "center",
    },
    {
      name: "Data de Atendimento",
      align: "center",
    },
    {
      name: "Carambola",
      minWidth: "110px",
      align: "center"
    },
    {
      name: "Maracujá",
      minWidth: "110px",
      align: "center"
    },
    {
      name: "",
      minWidth: "60px",
      align: "center"
    }
  ]

  const smallScreenColumns: ColumnProps[] = [
    {
      name: <CustomCheckBox handleToggle={handleToggleAllSelected} align='center' checked={isCheckedAll === true} />,
      align: "center",
      minWidth: "80px"
    },
    {
      name: "Motorista",
      align: "left",
    },
    {
      name: "Rota",
      align: "center",
    },
    {
      name: "CD",
      align: "center",
    },
    {
      name: "Data",
      align: "center",
    },
    {
      name: "Carambola",
      minWidth: "90px",
      align: "center"
    },
    {
      name: "Maracujá",
      minWidth: "90px",
      align: "center"
    },
    {
      name: "",
      minWidth: "60px",
      align: "center"
    }
  ]

  let columns = smallScreen ? smallScreenColumns : defaultColumns

  useEffect(() => {
    fetchData();
  }, [params.deliveryDate]);

  return (
    <>
    <ConfirmExportPopup
      popup={confirmPopup}
      onClose={() => setConfirmPopup(prev => ({ ...prev, visible: false }))}
      onConfirm={handleConfirmExport}
    />
    <div className="pageContent">
      <PageContainer>
        <MainContainer>
          <div className="infos">
            <h3>Exportação de Rotas</h3>
            <Toolbar
              loadingState={loading}
              placeholder="Motorista, veículo ou rota"
              alignButtonsToEnd
              onChangeSearch={handleSearch}
              page="ExportRoutes"
              params={params}
              paramState={handleChangeParams}
              extraButtons={[
                {
                  type: "filled",
                  text: `Exportar ${selectedRows.length ? `(${selectedRows.length})` : ""}`,
                  onClick: handleExport,
                  disableState: selectedRows.length === 0 || waitingCarambola || waitingMaracuja,
                  customWidth: "150px",
                  customMargin: "0 8px 0 0"
                },
                {
                  type: "outlined",
                  text: loadingCounters ? "Atualizando contadores..." : "Atualizar contadores",
                  icon: <RefreshIcon sx={{ fontSize: 16 }} />,
                  onClick: refreshCounters,
                  disableState: loadingCounters,
                  customWidth: "190px",
                  customMargin: "0 24px 0 0"
                }
              ]}
            />
          </div>
          <DataTable loading={loading} columns={columns}>
            {filteredData
              .sort((a: IAgileRoute, b: IAgileRoute) => a.routeId - b.routeId)
              .map(element => (
                <FormTableLine
                  key={element.routeId}
                  element={element}
                  handleToggleSelected={handleToggleSelected}
                  selectedRows={selectedRows}
                  waitingCarambola={waitingCarambola}
                  waitingMaracuja={waitingMaracuja}
                  responsesCarambola={responsesCarambola}
                  responsesMaracuja={responsesMaracuja}
                  dataOrderRoute={dataOrderRoute}
                  maracujaRouteCounts={maracujaRouteCounts}
                />
              ))}
          </DataTable>
          <div className="spacer" />
        </MainContainer>
      </PageContainer>
    </div>
    </>
  );
};
